import {
  Button,
  ButtonGroup,
  Grid,
  GridCell,
  Icon,
  IconButton,
  Input,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  SelectWC,
  sendToast,
  Spacer,
  Stack,
  Tag,
  TextBody,
  TextTitle,
  Tooltip,
} from '@pypestream/design-system';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import './reporting-api-credential-modal.scss';

import {
  getChannelsFromPypes,
  getEnvDotColor,
  getEnvDotStyle,
} from '../../../utils';
import {
  getChannelOptions,
  getSolutionOptions,
} from './credential-modal-options';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
} from '../../../xstate/app.xstate';
import { useGoToChannel } from '../../../pages/project-settings/use-go-to-channel';
import { GenericModal, GenericModalProps } from '..';
import { TranslationComponent, useTranslation } from '@pypestream/translations';

type Env = 'live' | 'sandbox';

// The legacy grey redirect tag pill, as in the Reporting API Credentials and
// Data Privacy channel lists. The Live Agent Platforms "Channels Using It"
// column no longer uses it: since PSTUDIO-4343 it renders solution boxes.
const PILL_STYLE = {
  ['--ps-tag-bg-color' as string]: 'rgba(98, 105, 124, 0.05)',
  ['--ps-tag-bg-hover-color' as string]: 'rgba(98, 105, 124, 0.1)',
};

export type ReportingApiCredentialModalProps = GenericModalProps & {
  type?: 'add' | 'edit';
  env?: Env;
  /** The key id being edited (undefined for add). */
  credentialId?: string;
  /** The pype the edited credential belongs to. */
  pypeId?: string;
  /**
   * Bumped by the hook on every open. The modal element stays mounted across
   * opens (only its `open` property flips), so this is the only signal the React
   * tree gets that a fresh open happened — used to place the initial focus.
   */
  openSeq?: number;
};

interface FormValues {
  name: string;
  description: string;
  pypeId: string;
  env: Env;
}

export const ReportingApiCredentialModal: FC<
  ReportingApiCredentialModalProps
> = ({
  type = 'add',
  env = 'live',
  credentialId,
  pypeId,
  openSeq,
  modalRef,
  onClose,
}) => {
  const { t } = useTranslation();
  const isEdit = type === 'edit';

  const { pypes, credentials, solutions } = useProStudioCtxSelector((ctx) => ({
    pypes: ctx.liveAgentPypes,
    credentials: ctx.apiCredentials,
    solutions: ctx.solutions,
  }));

  const editingCredential = useMemo(
    () =>
      credentials?.[env]?.find((credential) => credential.id === credentialId),
    [credentials, env, credentialId]
  );

  const goToChannel = useGoToChannel();
  const channels = editingCredential?.channels ?? [];

  // After a successful create the backend returns the key + secret ONCE; hold
  // them here to reveal the "Save Your Reporting API Credentials" step before
  // closing. Cleared on close so the next open starts fresh.
  const [createdKey, setCreatedKey] = useState<{
    key: string;
    secret: string;
  } | null>(null);

  const handleClose = () => {
    setCreatedKey(null);
    setIsSubmitting(false);
    onClose?.();
  };

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', description: '', pypeId: '', env },
  });

  /**
   * The `botSolutionId` picked in the Solution field — deliberately NOT a form
   * field: it scopes the Channel list but is not part of the credential, which is
   * pype-scoped. Keeping it out of `useForm` also keeps a `required` rule from
   * leaking into the edit flow, where the field isn't rendered but a registration
   * would survive (the modal element is shared between add and edit).
   *
   * Never pre-filled from the breadcrumb solution: the create flow is "pick the
   * solution, then the channel", and Channel stays disabled until one is chosen
   * (PSTUDIO-3829).
   */
  const [selectedSolutionId, setSelectedSolutionId] = useState('');

  useEffect(() => {
    reset({
      name: editingCredential?.name ?? '',
      description: editingCredential?.description ?? '',
      pypeId: editingCredential?.pypeId ?? pypeId ?? '',
      env,
    });
    setSelectedSolutionId('');
    setIsSubmitting(false);
  }, [reset, editingCredential, pypeId, env, type, credentialId]);

  const selectedEnv = watch('env');

  const solutionOptions = useMemo(
    () => getSolutionOptions(solutions),
    [solutions]
  );

  const connectionOptions = useMemo(
    () =>
      getChannelOptions(
        getChannelsFromPypes(pypes?.[selectedEnv] ?? []),
        selectedSolutionId
      ),
    [pypes, selectedEnv, selectedSolutionId]
  );

  const solutionSelectRef = useRef<SelectWC>(null);

  /**
   * Initial focus goes to Solution, not Credential Name (PSTUDIO-3829) — it is the
   * first choice the rest of the form depends on.
   *
   * `ps-modal` activates a focus trap once its open transition ends, which focuses
   * the first tabbable node (the close button). There is no "opened" event to hang
   * this off, and the timing is animation-dependent, so instead of racing it with a
   * timeout we let it focus whatever it wants and move focus on the first `focusin`
   * the modal sees. `focusin` is composed, so it reaches the host from the shadow
   * DOM close button too.
   */
  useEffect(() => {
    if (!openSeq || isEdit) return undefined;

    const modalEl = modalRef?.current as unknown as HTMLElement | null;
    if (!modalEl) return undefined;

    const takeFocus = () => {
      modalEl.removeEventListener('focusin', takeFocus);
      // `focus()` reaches into the select's shadow DOM for its combobox input, so
      // wait for the render that creates it rather than assuming it is there.
      const select = solutionSelectRef.current;
      void select?.updateComplete.then(() => select.focus());
    };

    modalEl.addEventListener('focusin', takeFocus);
    return () => modalEl.removeEventListener('focusin', takeFocus);
  }, [openSeq, isEdit, modalRef]);

  // Guards against a double-click minting two credentials: the dispatch is
  // fire-and-forget, so nothing stopped a second click sending another
  // `createApiCredential` before the first returned.
  //
  // Cleared on close and whenever the modal is (re)opened for a different
  // credential — see the `reset` effect below. A failed mutation invokes no
  // callback, so after an error the user re-opens the modal to retry rather than
  // clicking Create again; Cancel stays enabled throughout, so this can't wedge.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = handleSubmit((values) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    if (isEdit && credentialId) {
      sendProStudioEvent({
        type: 'updateApiCredential',
        payload: {
          id: credentialId,
          pypeId: values.pypeId,
          name: values.name,
          description: values.description,
          env: values.env,
          cb: handleClose,
        },
      });
    } else {
      sendProStudioEvent({
        type: 'createApiCredential',
        payload: {
          name: values.name,
          description: values.description,
          pypeId: values.pypeId,
          env: values.env,
          // Reveal the key + secret instead of closing; if the backend didn't
          // return them, just close.
          cb: (created) => (created ? setCreatedKey(created) : handleClose()),
        },
      });
    }
  });

  if (createdKey) {
    // Await the write and report the actual outcome. The key is shown exactly
    // once, so a "Copied to clipboard" toast on a rejected write (denied
    // permission, non-secure context, unfocused document) would send the user
    // away believing they had saved a secret they can never see again.
    const copy = async (value: string) => {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        sendToast({
          toastId: 'reporting-api-credential-copy-failed',
          type: 'error',
          text: 'Could not copy — select the value and copy it manually',
        });
        return;
      }

      sendToast({
        toastId: 'reporting-api-credential-copied',
        type: 'success',
        text: 'Copied to clipboard',
        autoClose: 2000,
      });
    };

    return (
      <GenericModal
        test-id="reporting-api-credential-modal"
        modalRef={modalRef}
        onClose={handleClose}
        size="large"
      >
        <TextTitle size="small" textAlign="center">
          Save Your Reporting API Credentials
        </TextTitle>
        <Spacer size="small" />
        <TextBody variant="secondary" textAlign="center">
          Your credential is ready. Copy the key and secret below.
        </TextBody>
        <Spacer size="xlarge" />

        <Grid>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Key</TextBody>
            <Spacer size="2xsmall" />
            <div
              className="c-secret-field"
              data-test-id="reporting-api-credential-modal-created-key"
            >
              <span className="c-secret-field__value" title={createdKey.key}>
                {createdKey.key}
              </span>
              <IconButton
                name="copy"
                size="small"
                onClick={() => copy(createdKey.key)}
              />
            </div>
          </GridCell>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Secret</TextBody>
            <Spacer size="2xsmall" />
            <div
              className="c-secret-field"
              data-test-id="reporting-api-credential-modal-created-secret"
            >
              <span className="c-secret-field__value" title={createdKey.secret}>
                {createdKey.secret}
              </span>
              <IconButton
                name="copy"
                size="small"
                onClick={() => copy(createdKey.secret)}
              />
            </div>
          </GridCell>
        </Grid>

        <Spacer size="medium" />
        <div className="c-save-secret-warning-banner" role="alert">
          <span className="c-save-secret-warning-banner__icon">
            <Icon name="alert" color="gold" size="medium" />
          </span>
          <div className="c-save-secret-warning-banner__text">
            <TextBody size="small">
              Save your secret before closing. This is the only time it will be
              displayed. If lost, you&apos;ll need to regenerate the credential
            </TextBody>
          </div>
        </div>

        <Stack slot="footer" justifyContent="end" style={{ width: '100%' }}>
          <Button
            test-id="reporting-api-credential-modal-saved-secret"
            size="large"
            onClick={handleClose}
          >
            I&apos;ve saved my secret
          </Button>
        </Stack>
      </GenericModal>
    );
  }

  /**
   * Hoisted so the Tooltip below can wrap it without the JSX being written twice.
   * Only rendered by the create branch.
   */
  const channelSelect = (
    <Select
      test-id="reporting-api-credential-modal-connection"
      variant="outlined"
      placeholder="Select a channel"
      disabled={!selectedSolutionId}
      data={connectionOptions}
      searchKeys={['label']}
      hasError={!!errors.pypeId}
      value={watch('pypeId')}
      {...register('pypeId', { required: true })}
      onChange={(e) => {
        const { value } = e.target as { value: string };
        setValue('pypeId', value);
      }}
    >
      {connectionOptions.map((option) => (
        <SelectOption key={option.id} value={option.id}>
          {option.label}
        </SelectOption>
      ))}
    </Select>
  );

  return (
    <GenericModal
      test-id="reporting-api-credential-modal"
      modalRef={modalRef}
      onClose={handleClose}
      size="large"
    >
      <TextTitle size="small" textAlign="center">
        {isEdit ? 'Edit Credential Details' : 'Create Reporting API Credential'}
      </TextTitle>
      <Spacer size="small" />
      <TextBody variant="secondary" textAlign="center">
        {isEdit
          ? 'The key, secret and channel assignment cannot be changed.'
          : "The solution, channel and environment you choose can't be changed after the credential is created."}
      </TextBody>
      <Spacer size="xlarge" />

      <Grid>
        <GridCell xsmall="12">
          <TextBody size="small">Credential Name</TextBody>
          <Spacer size="2xsmall" />
          <Input
            test-id="reporting-api-credential-modal-name"
            variant="outlined"
            placeholder="Credential name"
            // The field is `required`, so without this the rejection is
            // INVISIBLE: Create simply does nothing — no error, no spinner (the
            // submit handler never runs, so `isSubmitting` never flips), no
            // request. The two Selects below already surface `errors.pypeId`
            // this way; the name was the one required field that did not.
            hasError={!!errors.name}
            {...register('name', { required: true })}
          />
        </GridCell>

        <GridCell xsmall="12">
          <Stack gutter="2xsmall" alignItems="center" nowrap>
            <TextBody size="small">Description</TextBody>
            <TextBody size="small" variant="secondary">
              (optional)
            </TextBody>
          </Stack>
          <Spacer size="2xsmall" />
          <Input
            test-id="reporting-api-credential-modal-description"
            variant="outlined"
            placeholder="What this credential is used for"
            {...register('description')}
          />
        </GridCell>

        {isEdit ? (
          <>
            {channels.length > 0 && (
              <GridCell xsmall="12">
                <TextBody size="small">
                  Channels Using This Credential:
                </TextBody>
                <Spacer size="2xsmall" />
                <Stack gutter="xsmall" style={{ flexWrap: 'wrap' }}>
                  {channels.map((channel) => (
                    <Tooltip key={channel.channelId} content="Go to channel">
                      <Tag
                        size="small"
                        minimal
                        interactive
                        style={PILL_STYLE}
                        onClick={() => goToChannel(channel)}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            flex: '0 0 auto',
                            background: getEnvDotColor(channel.env),
                          }}
                        />
                        {channel.name}
                        <Icon name="arrow-in-circle" size="xsmall" />
                      </Tag>
                    </Tooltip>
                  ))}
                </Stack>
              </GridCell>
            )}

            {/* Key/secret and channel assignment can't be changed on edit. */}
            <GridCell xsmall="12">
              <TextBody size="small">Key</TextBody>
              <Spacer size="2xsmall" />
              <Input
                test-id="reporting-api-credential-modal-key"
                variant="outlined"
                value={editingCredential?.id ?? ''}
                readonly
              />
            </GridCell>
          </>
        ) : (
          <>
            <GridCell xsmall="12">
              <TextBody size="small">Environment</TextBody>
              <Spacer size="2xsmall" />
              {/*
                The Sandbox/Live pill toggle every other settings surface uses
                (PSTUDIO-3829) — it was a plain <Select> here, which read as a
                third dropdown next to Solution and Channel.
              */}
              <SegmentedControl
                test-id="reporting-api-credential-modal-env"
                value={selectedEnv}
                onChange={(e) => {
                  setValue('env', e.detail.value as Env);
                  // A pype exists in one env only, so the picked channel can't
                  // survive the switch. The solution can — it is project-level.
                  setValue('pypeId', '');
                }}
              >
                <SegmentedControlItem value="sandbox">
                  <span
                    slot="prefix"
                    style={getEnvDotStyle('sandbox', selectedEnv === 'sandbox')}
                  />
                  Sandbox
                </SegmentedControlItem>
                <SegmentedControlItem value="live">
                  <span
                    slot="prefix"
                    style={getEnvDotStyle('live', selectedEnv === 'live')}
                  />
                  Live
                </SegmentedControlItem>
              </SegmentedControl>
            </GridCell>

            <GridCell xsmall="12" small="6">
              <TextBody size="small">
                <TranslationComponent i18nKey="pro-studio/common:vocabulary.solution">
                  Solution
                </TranslationComponent>
              </TextBody>
              <Spacer size="2xsmall" />
              {/*
                `data` + `searchKeys` are what turn on the Select's built-in
                typeahead, and they are passed unconditionally: some projects carry
                100+ solutions, so the search can't be gated on list size.
              */}
              <Select
                ref={solutionSelectRef}
                test-id="reporting-api-credential-modal-solution"
                variant="outlined"
                placeholder={
                  t('pro-studio/common:vocabulary.select_a_solution') || ''
                }
                data={solutionOptions}
                searchKeys={['label']}
                // Channel is `required` and a failed validation calls no callback,
                // so a Create click with nothing picked would otherwise look inert.
                // An empty Channel while no Solution is picked is this field's
                // fault, so it flags itself too.
                hasError={!!errors.pypeId && !selectedSolutionId}
                value={selectedSolutionId}
                onChange={(e) => {
                  const { value } = e.target as { value: string };
                  setSelectedSolutionId(value);
                  // Channels are scoped to the solution; the previous pick is not
                  // in the new list.
                  setValue('pypeId', '');
                }}
              >
                {solutionOptions.map((option) => (
                  <SelectOption key={option.id} value={option.id}>
                    {option.label}
                  </SelectOption>
                ))}
              </Select>
            </GridCell>

            <GridCell xsmall="12" small="6">
              <TextBody size="small">Channel</TextBody>
              <Spacer size="2xsmall" />
              {/*
                Disabled rather than merely empty until a Solution is picked, so the
                order the form has to be filled in is visible up front — with a
                tooltip while it is, so the reason is readable rather than guessed.

                The Tooltip is rendered ONLY while disabled: `ps-tooltip` has no
                `disabled` prop and an empty `content` still pops an empty bubble, so
                swapping it out is the only way to keep it silent once a Solution is
                picked. Hover still reaches it while disabled — `ps-select` sets no
                `pointer-events: none` (its disabled state is styling plus guarded
                handlers), and `ps-tooltip` listens for a bubbling `mouseover` on
                itself. It is `display: contents`, so the grid layout is unchanged.

                The swap happens inside a plain wrapper React fully owns — the shape
                `portaled-modal-sibling.test.tsx` pins for this page, since a branch
                that flips next to a node which relocated itself into a portal is what
                throws `insertBefore`.
              */}
              <div>
                {selectedSolutionId ? (
                  channelSelect
                ) : (
                  <Tooltip
                    content={
                      t(
                        'pro-studio/common:vocabulary.select_solution_for_channel'
                      ) || ''
                    }
                  >
                    {channelSelect}
                  </Tooltip>
                )}
              </div>
            </GridCell>
          </>
        )}
      </Grid>

      <ButtonGroup slot="footer">
        <Button
          test-id="reporting-api-credential-modal-cancel"
          width="full"
          variant="secondary"
          size="large"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          test-id="reporting-api-credential-modal-submit"
          width="full"
          size="large"
          disabled={isSubmitting}
          onClick={onSubmit}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
